import { Button, OdinIcon } from '@myosh/odin-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  BasicChartDataDto,
  RecordsAnalysisResultsDto,
  useGetWidgetsDataAnalysisResultQuery,
  useStartWidgetsDataAnalysisMutation,
  WidgetInsightsResponseDto,
} from '../../redux/config/widgetApi';
import { removeWidgetAiInsight, setWidgetAiInsight } from '../../redux/slices/widgetChart/WidgetChartSlice';

interface WidgetDataType {
  widgetId: string;
  body: BasicChartDataDto[] | undefined;
}

interface WidgetAiInsightPanelProps {
  widgetAiInsight: {
    title: string;
    show: boolean;
    value: WidgetInsightsResponseDto | RecordsAnalysisResultsDto | undefined;
    widgetData: WidgetDataType | undefined;
    isRecordInsight: boolean;
  };
}

const InsightItem = ({ label, value }: { label: string; value: string | null }) => (
  <p>
    <span className="font-bold">{label}: </span>
    {value ?? '-'}
  </p>
);

function WidgetAiInsightPanel({ widgetAiInsight }: WidgetAiInsightPanelProps) {
  const dispatch = useDispatch();
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  const { widgetData, isRecordInsight } = widgetAiInsight;
  const widgetId = widgetData?.widgetId;
  const body = widgetData?.body;
  const insightItem = widgetAiInsight?.value;

  const [jobId, setJobId] = useState<string>();
  const [errorMessage, setErrorMessage] = useState('');

  const [triggerWidgetsDataAnalysisJob, { isLoading: isTriggerLoading }] = useStartWidgetsDataAnalysisMutation();

  const { data: { status, result } = {}, isError } = useGetWidgetsDataAnalysisResultQuery(
    { analysisId: jobId! },
    {
      skip: !jobId,
      pollingInterval: jobId ? 5000 : 0, // 5 seconds polling interval
    }
  );

  const isLoading = !!jobId || isTriggerLoading;

  const handleClickClose = () => {
    dispatch(removeWidgetAiInsight());
    if (promptInputRef.current) {
      promptInputRef.current!.value = '';
    }
    setErrorMessage('');
  };

  const handleClickGenerateAiInsight = () => {
    setErrorMessage('');
    if (!widgetId || !body) {
      return;
    }
    dispatch(
      setWidgetAiInsight({
        value: undefined,
      })
    );

    triggerWidgetsDataAnalysisJob({
      widgetId: widgetId,
      widgetInsightsDataDto: {
        extraPrompt: promptInputRef.current?.value,
        data: body,
      },
    })
      .then((response) => {
        if ('error' in response) {
          setErrorMessage(t('generateAiInsightFailed'));
          return;
        }
        const jobId = response.data;
        if (jobId) {
          setJobId(jobId);
        } else {
          setErrorMessage(t('generateAiInsightFailed'));
        }
      })
      .catch(() => {
        setErrorMessage(t('generateAiInsightFailed'));
      });
  };

  const insightFields = !isRecordInsight
    ? [
        ['Data Analysis', (insightItem as WidgetInsightsResponseDto)?.data_analysis ?? null],
        ['Potential Causes', (insightItem as WidgetInsightsResponseDto)?.potential_causes ?? null],
        ['Recommendations', (insightItem as WidgetInsightsResponseDto)?.recommendations ?? null],
        ['Future Trends', (insightItem as WidgetInsightsResponseDto)?.future_trends ?? null],
        ['Missing Values Analysis', (insightItem as WidgetInsightsResponseDto)?.missing_values_analysis ?? null],
        ['Summary', (insightItem as WidgetInsightsResponseDto)?.summary ?? null],
      ]
    : [
        ['Data Analysis', (insightItem as RecordsAnalysisResultsDto)?.data_analysis ?? null],
        ['Root Cause', (insightItem as RecordsAnalysisResultsDto)?.root_cause ?? null],
        ['Frequency Trends', (insightItem as RecordsAnalysisResultsDto)?.frequency_trends ?? null],
        ['Benchmarking', (insightItem as RecordsAnalysisResultsDto)?.benchmarking ?? null],
        ['Recommendations', (insightItem as RecordsAnalysisResultsDto)?.recommendations ?? null],
      ];

  useEffect(() => {
    setErrorMessage('');
  }, [widgetId]);

  useEffect(() => {
    if ('FINISHED' === status) {
      dispatch(
        setWidgetAiInsight({
          value: result,
        })
      );
      setJobId(undefined);
    } else if ('FAILED' === status || isError) {
      setErrorMessage(t('generateAiInsightFailed'));
      setJobId(undefined);
    }
  }, [status, isError, dispatch, result]);

  return (
    <div
      className={classNames('flex w-[600px] flex-col gap-4 overflow-y-auto border-s-[1px] p-4', {
        hidden: !widgetAiInsight.show,
      })}
    >
      <div className="flex flex-row justify-between">
        <p className="text-xl font-bold">{`${isRecordInsight ? 'Record' : 'AI'} Insight`}</p>
        <div title="Close" className="cursor-pointer" onClick={handleClickClose}>
          <OdinIcon icon="X" />
        </div>
      </div>

      <p className="text-lg font-semibold">{widgetAiInsight.title}</p>
      <hr />

      {!isRecordInsight && (
        <>
          <div className="min-h-32 flex">
            <textarea
              ref={promptInputRef}
              name="custom-instructions"
              placeholder="What do you want to know more about?"
              className="h-full w-full resize-none appearance-none border border-gray-4 px-2 py-1 text-sm focus:outline-none"
              cols={30}
              rows={5}
              disabled={isLoading}
            />
          </div>

          <Button
            onClick={handleClickGenerateAiInsight}
            type={'primary'}
            fullWidth={true}
            htmlType="button"
            disabled={isLoading}
          >
            Generate AI Insight
          </Button>

          {isLoading && <p>Generating AI Insight...</p>}
        </>
      )}

      {isError && errorMessage && <p>{errorMessage}</p>}

      {!isEmpty(insightItem) &&
        insightFields.map(([label, fieldValue]) => (
          <InsightItem key={label} label={label as string} value={fieldValue} />
        ))}
    </div>
  );
}

export default WidgetAiInsightPanel;
